import { ignoredFormKeyCodes } from "../../ignored_form_key_codes";
import { cardNumberSpecFor, formattedLengthFor, spacePositionsFor } from "./card_brand";

// Longest possible formatted value across supported brands (16-digit PANs formatted as 4-4-4-4)
export const MAX_FORMATTED_CC_NUM_LENGTH = 19;

const NUM_KEYS = new Array(10).fill(null).map((_, i) => i.toString());
const ALLOWED_KEYS_NUMERIC = [...NUM_KEYS, ...ignoredFormKeyCodes];

export const creditCardNumberInputPartialValidator = /^[\d\s]+$/;

export const creditCardNumberInputValidator = (value) => {
  const spec = cardNumberSpecFor(value);
  const blockPattern = spec.blockSizes.map((size) => `[\\d]{${size}}`).join("\\s");
  return new RegExp(`^${blockPattern}$`).test(value);
};

export const isCursorMovementKey = (key) => /arrow|home|end/i.test(key);

export function isInSpaceChar (num, spec) {
  return spacePositionsFor(spec).includes(num);
}

export function creditCardNumberExtractor (value) {
  const cleanChars = value.split("")
    .filter((char) => /^\d$/.test(char))
    .join("");

  const spec = cardNumberSpecFor(cleanChars);

  return cleanChars.slice(0, spec.length);
}

export function creditCardNumberFormatter (value) {
  const cleanChars = creditCardNumberExtractor(value);
  const spec = cardNumberSpecFor(cleanChars);

  const groups = [];
  let index = 0;
  spec.blockSizes.forEach((blockSize) => {
    if (index >= cleanChars.length) return;
    groups.push(cleanChars.slice(index, index + blockSize));
    index += blockSize;
  });

  return groups.join(" ");
}

export function handleCCInputEmptySpace (event) {
  const { key, target: { value, selectionStart, selectionEnd } } = event;

  const target = {
    value: value.slice(0),
    selectionStart,
    selectionEnd
  };

  const spec = cardNumberSpecFor(value);
  const maxBlockSize = Math.max(...spec.blockSizes);

  // if selection is diff it is selecting text, if is cursor movement commands skips
  // eslint-disable-next-line eqeqeq
  if (selectionStart == selectionEnd && !isCursorMovementKey(key)) {
    // handle cases where user is dealing with a space
    if (isInSpaceChar(selectionStart + 1, spec) && isInSpaceChar(selectionEnd + 1, spec)) {
      // case: remove space before cursor w/ backspace, remove char before space and move cursor 2 chars before
      if (/backspace/i.test(key)) {
        target.value = value
          .split("")
          .filter((_, idx) => idx !== (selectionStart - 1))
          .join("");
        target.selectionStart -= 2;
        target.selectionEnd -= 2;
        return target;
      }

      // case: remove space after cursor w/ delete, remove char after space and keep cursor position
      if (/delete/i.test(key)) {
        target.value = value
          .split("")
          .filter((_, idx) => idx !== selectionStart)
          .join("");

        return target;
      }
    }
  }
  // in case it is updating a digit block moving a space, just move the caret an extra character forward
  if (value.slice(value.slice(0, selectionEnd).lastIndexOf(" "), selectionEnd + 1).length > maxBlockSize + 1) {
    target.selectionStart += 1;
    target.selectionEnd += 1;
  }

  return target;
}

export function ccNumKeydownHandler (event) {
  if (event.key == 'Enter') {
    event.stopPropagation();
    event.preventDefault();
    return;
  }
  if (![event.metaKey, event.altKey, event.ctrlKey].some(active => active)) {
    if (!ALLOWED_KEYS_NUMERIC.includes(event.key) && !ignoredFormKeyCodes.includes(event.keyCode)) {
      event.preventDefault();
      return;
    }
    if (ignoredFormKeyCodes.includes(event.keyCode)) {
      return;
    }
  }

  const { value, selectionStart, selectionEnd } = event.target;

  const formattedLength = formattedLengthFor(cardNumberSpecFor(value));
  const slicedValue = value.slice(0, formattedLength);

  event.target.value = slicedValue;

  this.value = slicedValue; // `this` here points to the input HTML Element

  event.target.setSelectionRange(selectionStart, selectionEnd);
}

export function ccNumKeyupHandler (event) {
  const updatedTarget = handleCCInputEmptySpace(event);

  const { value } = updatedTarget;
  if (!value.length) {
    return;
  }

  const updatedValue = creditCardNumberFormatter(value);
  const formattedLength = formattedLengthFor(cardNumberSpecFor(updatedValue || value));

  const shouldJumpToNextField =
    !ignoredFormKeyCodes.includes(event.keyCode) &&
    updatedTarget.value.length >= formattedLength &&
    updatedTarget.selectionStart >= formattedLength &&
    updatedTarget.selectionEnd >= formattedLength;

  // eslint-disable-next-line eqeqeq
  const isContentDiff = updatedValue != value;
  if (!isContentDiff) {
    if (shouldJumpToNextField) {
      event.target.nextElementSibling.focus();
    }
    return;
  }
  const isFieldOverflowing = value.length >= formattedLength;
  const sizeDiff = updatedValue.length - value.length;

  this.value = updatedValue.slice(0, formattedLength); // `this` here points to the input HTML Element

  const caretPositionDiff = isFieldOverflowing ? 0 : sizeDiff;

  event.target.setSelectionRange(
    updatedTarget.selectionStart + caretPositionDiff,
    updatedTarget.selectionEnd + caretPositionDiff
  );

  if (shouldJumpToNextField) {
    event.target.nextElementSibling.focus();
  }
}

function ccNumPasteHandler (event) {
  const pasteData = event.clipboardData.getData("text");
  event.preventDefault(pasteData);
  this.value = creditCardNumberFormatter(pasteData);
}

export function setupCreditCardNumberInput (inputElement) {
  inputElement.addEventListener("keydown", ccNumKeydownHandler);
  inputElement.addEventListener("keyup", ccNumKeyupHandler);
  inputElement.addEventListener("paste", ccNumPasteHandler);

  inputElement.setAttribute("maxlength", MAX_FORMATTED_CC_NUM_LENGTH);
  inputElement.setAttribute(
    "pattern",
    "^([0-9]{4}\\s[0-9]{4}\\s[0-9]{4}\\s[0-9]{4}|[0-9]{4}\\s[0-9]{6}\\s[0-9]{5})$"
  );

  const proxyHandler = {
    get (target, prop, _receiver) {
      switch (prop) {
        case "value":
          return creditCardNumberExtractor(target.value ?? "");
        case "valid":
          return creditCardNumberInputValidator(target.value ?? "");
        case "_self":
          return target;
        default:
          return Reflect.get(...arguments);
      }
    },
    set (target, prop, value, _receiver) {
      if (prop === "value") {
        target.value = creditCardNumberFormatter(value.toString());
      } else {
        return Reflect.set(...arguments);
      }
      return true; // spec requires return of true in strict-mode
    }
  };

  return new Proxy(inputElement, proxyHandler);
}
